/**
 * The Scriptable script for the iPhone widget (8 October 2026), filled in
 * with this dashboard's address and the widget key. Scriptable is a free
 * iOS app that runs a script as a home-screen or lock-screen widget; this
 * one reads GET /api/widget (shell/api/src/widget.ts) and draws the ticker.
 *
 * iOS decides when widgets refresh (roughly every 15 to 60 minutes); the
 * script asks for 15. Tapping the widget opens the dashboard.
 */
export function scriptableWidget(origin: string, token: string): string {
  return `// A.C.C.R.A widget for Scriptable (Addaiaspora Command Centre for Real-time Analytics)
// Read-only: running totals and the latest event types. No names, no places.
// Made by the dashboard on ${new Date().toISOString().slice(0, 10)}. Keep this key private.
const ORIGIN = ${JSON.stringify(origin)};
const KEY = ${JSON.stringify(token)};

const TEAL = new Color("#37d7ff");
const MUTED = new Color("#90959b");
const UP = new Color("#3ccf8e");

async function load() {
  const req = new Request(ORIGIN + "/api/widget");
  req.headers = { Authorization: "Bearer " + KEY };
  req.timeoutInterval = 20;
  try {
    const data = await req.loadJSON();
    return data && data.lines ? { data } : { error: (data && data.error) || "No data" };
  } catch (e) {
    return { error: "Offline: " + e };
  }
}

function time(iso) {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

function ago(iso) {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 60) return m + "m";
  const h = Math.round(m / 60);
  return h < 48 ? h + "h" : Math.round(h / 24) + "d";
}

function row(w, line, size, useShort) {
  const s = w.addStack();
  s.centerAlignContent();
  const dot = s.addText("●");
  dot.textColor = new Color(line.colour);
  dot.font = Font.systemFont(size - 3);
  s.addSpacer(4);
  const label = s.addText(useShort ? line.short : line.label);
  label.textColor = MUTED;
  label.font = Font.systemFont(size - 1);
  label.lineLimit = 1;
  s.addSpacer();
  const value = s.addText(line.value);
  value.textColor = Color.white();
  value.font = Font.semiboldSystemFont(size);
  value.lineLimit = 1;
  if (line.change) {
    s.addSpacer(3);
    const ch = s.addText(line.change);
    ch.textColor = UP;
    ch.font = Font.mediumSystemFont(size - 2);
    ch.lineLimit = 1;
  }
}

const family = config.widgetFamily || "large";
const { data, error } = await load();
const w = new ListWidget();
w.url = ORIGIN + "/";
w.refreshAfterDate = new Date(Date.now() + 15 * 60 * 1000);

if (family.startsWith("accessory")) {
  // Lock screen: the first lines, as many as fit.
  if (error) {
    w.addText("Accra: " + error).font = Font.systemFont(11);
  } else {
    const n = family === "accessoryRectangular" ? 3 : 1;
    for (const line of data.lines.slice(0, n)) {
      const t = w.addText(line.short + " " + line.value + (line.change ? " " + line.change : ""));
      t.font = Font.systemFont(12);
      t.lineLimit = 1;
    }
  }
} else {
  w.backgroundColor = new Color("#0d0d0d");
  w.setPadding(12, 14, 12, 14);
  const head = w.addStack();
  head.centerAlignContent();
  const title = head.addText("A.C.C.R.A");
  title.textColor = TEAL;
  title.font = Font.boldSystemFont(11);
  head.addSpacer();
  const stamp = head.addText(data ? time(data.updatedAt) : "");
  stamp.textColor = MUTED;
  stamp.font = Font.systemFont(10);
  w.addSpacer(6);

  if (error) {
    const t = w.addText(error);
    t.textColor = MUTED;
    t.font = Font.systemFont(11);
  } else {
    const limits = { small: 5, medium: 6, large: 13, extraLarge: 13 };
    const size = family === "small" ? 11 : 12;
    for (const line of data.lines.slice(0, limits[family] || 6)) {
      row(w, line, size, family === "small");
      w.addSpacer(family === "small" ? 2 : 3);
    }
    if ((family === "large" || family === "extraLarge") && data.latest.length) {
      w.addSpacer(6);
      const h = w.addText("LATEST");
      h.textColor = MUTED;
      h.font = Font.semiboldSystemFont(9);
      w.addSpacer(3);
      for (const e of data.latest) {
        const s = w.addStack();
        s.centerAlignContent();
        const dot = s.addText("●");
        dot.textColor = new Color(e.colour);
        dot.font = Font.systemFont(8);
        s.addSpacer(4);
        const t = s.addText(e.what);
        t.textColor = Color.white();
        t.font = Font.systemFont(11);
        s.addSpacer();
        const a = s.addText(ago(e.at));
        a.textColor = MUTED;
        a.font = Font.systemFont(10);
        w.addSpacer(2);
      }
    }
  }
  w.addSpacer();
}

Script.setWidget(w);
if (!config.runsInWidget) await w.presentLarge();
Script.complete();
`;
}
